import {
  Box,
  HStack,
  Icon,
  IconButton,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { DeleteIcon } from '@diamond/shared/icons';
import { showToast } from '@diamond/shared/ui';
import { CloudUpload, FilePresentOutlined } from '@mui/icons-material';
import { useCallback, useState } from 'react';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import { FieldErrors, FieldValues, useFormContext } from 'react-hook-form';

interface ShipToDropzoneProps<T extends FieldValues = FieldValues> {
  name: string;
  accept?: Accept;
  maxSize?: number;
  errors?: FieldErrors<T>;
  placeholder?: string;
  isUploading?: boolean;
  onUpload?: (files: File[]) => void;
  onRemove?: () => void;
}

export function ShipToDropzone<T extends FieldValues = FieldValues>({
  name,
  accept = {
    'text/csv': ['.csv'],
    'application/vnd.ms-excel': ['.csv'],
  },
  maxSize = 2,
  errors,
  placeholder = 'Drag and drop file here or browse',
  isUploading = false,
  onUpload,
  onRemove,
}: ShipToDropzoneProps<T>) {
  const { setValue, watch } = useFormContext();
  const toast = useToast();
  const files: File[] = (watch(name) as File[]) || [];
  const [isDragActive, setIsDragActive] = useState(false);

  const errorMessage = errors ? (errors[name]?.message as string) : '';

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors.some((e) => e.code === 'file-too-large')) {
          showToast(
            toast,
            'error',
            `File terlalu besar. Maksimal ${maxSize}MB`
          );
        } else if (
          rejection.errors.some((e) => e.code === 'file-invalid-type')
        ) {
          showToast(
            toast,
            'error',
            'Format file tidak didukung. Gunakan file CSV'
          );
        }
        return;
      }

      if (acceptedFiles.length > 0) {
        // If onUpload callback provided, use it (for upload flow)
        // Otherwise, just set the value (for local file handling)
        if (onUpload) {
          onUpload(acceptedFiles);
        } else {
          setValue(name, acceptedFiles, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      }
    },
    [maxSize, name, setValue, toast, onUpload]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept,
    maxFiles: 1,
    maxSize: maxSize * 1024 * 1024,
    noClick: true,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDropAccepted: () => setIsDragActive(false),
    onDropRejected: () => setIsDragActive(false),
  });

  const removeFile = () => {
    if (onRemove) {
      onRemove();
    } else {
      setValue(name, [], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const hasFile = files.length > 0;

  if (hasFile) {
    const file = files[0];
    return (
      <HStack
        spacing={3}
        align="start"
        border="1px"
        borderColor="gray.200"
        borderRadius="md"
        p={3}
        bg="gray.50"
        w="full"
      >
        <Box
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          p={2}
          bg="white"
          minW="40px"
          h="40px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <FilePresentOutlined fontSize="small" />
        </Box>

        <VStack align="start" spacing={1} flex={1}>
          <Text fontSize="sm" fontWeight="medium" color="gray.800">
            {file.name}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {(file.size / 1024).toFixed(1)} KB
          </Text>
          {errorMessage && (
            <Text fontSize="xs" color="red.500">
              {errorMessage}
            </Text>
          )}
        </VStack>

        <IconButton
          aria-label="Remove file"
          icon={<DeleteIcon />}
          size="sm"
          variant="ghost"
          colorScheme="red"
          onClick={removeFile}
        />
      </HStack>
    );
  }

  return (
    <Box
      {...getRootProps()}
      border="2px dashed"
      borderColor={isDragActive ? 'blue.400' : 'gray.200'}
      borderRadius="lg"
      p={6}
      bg={isDragActive ? 'blue.50' : 'white'}
      textAlign="center"
      cursor={isUploading ? 'not-allowed' : 'pointer'}
      opacity={isUploading ? 0.6 : 1}
      onClick={isUploading ? undefined : open}
      pointerEvents={isUploading ? 'none' : 'auto'}
    >
      <input {...getInputProps()} disabled={isUploading} />
      <VStack spacing={2}>
        <Icon as={CloudUpload} boxSize={6} color="blue.400" />
        <Text fontSize="sm" color="gray.600">
          {isUploading ? 'Uploading...' : placeholder}
        </Text>
        <Text fontSize="xs" color="gray.500">
          Maksimal {maxSize}MB per upload
        </Text>
        {errorMessage && (
          <Text fontSize="xs" color="red.500">
            {errorMessage}
          </Text>
        )}
      </VStack>
    </Box>
  );
}
